import {
  ControlModeTemplateField,
  ControlModeTemplateFieldsType,
  ControlModeTemplateSettings,
} from '@src/types/ControlModes';

// Define a type for form values
export type FormValue = string | number | boolean | null;

// Define a type for delete item
export type DeleteItem = ControlModeTemplateField | string;

// Define action type enum
export enum FormActionType {
  SET_NAME = 'SET_NAME',
  SET_FORM_VALUES = 'SET_FORM_VALUES',
  UPDATE_FORM_VALUE = 'UPDATE_FORM_VALUE',
  SET_ORIGINAL_VALUES = 'SET_ORIGINAL_VALUES',
  SET_UNSAVED_SETTINGS = 'SET_UNSAVED_SETTINGS',
  SET_GROUPED_FIELDS = 'SET_GROUPED_FIELDS',
  TOGGLE_ADD_FIELD = 'TOGGLE_ADD_FIELD',
  SET_SHOW_ADD_FIELD = 'SET_SHOW_ADD_FIELD',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_EDITING_FIELD = 'SET_EDITING_FIELD',
  SET_SELECTED_SETTING_INDEX = 'SET_SELECTED_SETTING_INDEX',
  SET_NEW_FIELD_DATA = 'SET_NEW_FIELD_DATA',
  RESET_NEW_FIELD_DATA = 'RESET_NEW_FIELD_DATA',
  SET_NEW_FIELD_ERRORS = 'SET_NEW_FIELD_ERRORS',
  SET_PARSED_OPTIONS = 'SET_PARSED_OPTIONS',
  SET_NEW_OPTION = 'SET_NEW_OPTION',
  ADD_OPTION = 'ADD_OPTION',
  REMOVE_OPTION = 'REMOVE_OPTION',
  SET_NEW_SETTING_DATA = 'SET_NEW_SETTING_DATA',
  RESET_NEW_SETTING_DATA = 'RESET_NEW_SETTING_DATA',
  SET_NEW_SETTING_ERRORS = 'SET_NEW_SETTING_ERRORS',
  TOGGLE_ADD_SETTING = 'TOGGLE_ADD_SETTING',
  SET_SHOW_ADD_SETTING = 'SET_SHOW_ADD_SETTING',
  SET_IS_SETTING_EDIT_MODE = 'SET_IS_SETTING_EDIT_MODE',
  SET_EDITING_SETTING = 'SET_EDITING_SETTING',
  SET_PENDING_SETTING = 'SET_PENDING_SETTING',
  SET_HAS_ATTEMPTED_SUBMIT = 'SET_HAS_ATTEMPTED_SUBMIT',
  SET_IS_SAVING = 'SET_IS_SAVING',
  SET_FIELD_ERRORS = 'SET_FIELD_ERRORS',
  SHOW_SNACKBAR = 'SHOW_SNACKBAR',
  HIDE_SNACKBAR = 'HIDE_SNACKBAR',
  SHOW_DELETE_CONFIRM = 'SHOW_DELETE_CONFIRM',
  HIDE_DELETE_CONFIRM = 'HIDE_DELETE_CONFIRM',
  ADD_NEW_FIELD = 'ADD_NEW_FIELD',
  ADD_NEW_SETTING = 'ADD_NEW_SETTING',
  DELETE_FIELD = 'DELETE_FIELD',
  DELETE_SETTING = 'DELETE_SETTING',
  TOGGLE_QUICK_CONFIG = 'TOGGLE_QUICK_CONFIG',
  RESET_FORM = 'RESET_FORM',
}

// Define action types
export type FormAction =
  | { type: FormActionType.SET_NAME; payload: string }
  | { type: FormActionType.SET_FORM_VALUES; payload: Record<string, FormValue> }
  | {
      type: FormActionType.UPDATE_FORM_VALUE;
      payload: { name: string; value: FormValue; defaultValue: FormValue };
    }
  | {
      type: FormActionType.SET_ORIGINAL_VALUES;
      payload: Record<string, FormValue>;
    }
  | {
      type: FormActionType.SET_UNSAVED_SETTINGS;
      payload: ControlModeTemplateSettings[] | null;
    }
  | {
      type: FormActionType.SET_GROUPED_FIELDS;
      payload: FormState['groupedFields'];
    }
  | { type: FormActionType.TOGGLE_ADD_FIELD }
  | { type: FormActionType.SET_SHOW_ADD_FIELD; payload: boolean }
  | { type: FormActionType.SET_IS_EDIT_MODE; payload: boolean }
  | {
      type: FormActionType.SET_EDITING_FIELD;
      payload: ControlModeTemplateField | null;
    }
  | { type: FormActionType.SET_SELECTED_SETTING_INDEX; payload: number }
  | {
      type: FormActionType.SET_NEW_FIELD_DATA;
      payload: Partial<FormState['newFieldData']>;
    }
  | { type: FormActionType.RESET_NEW_FIELD_DATA }
  | {
      type: FormActionType.SET_NEW_FIELD_ERRORS;
      payload: Record<string, string>;
    }
  | { type: FormActionType.SET_PARSED_OPTIONS; payload: string[] }
  | { type: FormActionType.SET_NEW_OPTION; payload: string }
  | { type: FormActionType.ADD_OPTION }
  | { type: FormActionType.REMOVE_OPTION; payload: string }
  | {
      type: FormActionType.SET_NEW_SETTING_DATA;
      payload: Partial<FormState['newSettingData']>;
    }
  | { type: FormActionType.RESET_NEW_SETTING_DATA }
  | {
      type: FormActionType.SET_NEW_SETTING_ERRORS;
      payload: Record<string, string>;
    }
  | { type: FormActionType.TOGGLE_ADD_SETTING }
  | { type: FormActionType.SET_SHOW_ADD_SETTING; payload: boolean }
  | { type: FormActionType.SET_IS_SETTING_EDIT_MODE; payload: boolean }
  | {
      type: FormActionType.SET_EDITING_SETTING;
      payload: ControlModeTemplateSettings | null;
    }
  | {
      type: FormActionType.SET_PENDING_SETTING;
      payload: ControlModeTemplateSettings | null;
    }
  | { type: FormActionType.SET_HAS_ATTEMPTED_SUBMIT; payload: boolean }
  | { type: FormActionType.SET_IS_SAVING; payload: boolean }
  | { type: FormActionType.SET_FIELD_ERRORS; payload: Record<string, string> }
  | {
      type: FormActionType.SHOW_SNACKBAR;
      payload: { message: string; severity: FormState['snackbarSeverity'] };
    }
  | { type: FormActionType.HIDE_SNACKBAR; payload?: { id: string } }
  | {
      type: FormActionType.SHOW_DELETE_CONFIRM;
      payload: { itemType: 'field' | 'setting'; item: DeleteItem };
    }
  | { type: FormActionType.HIDE_DELETE_CONFIRM }
  | { type: FormActionType.ADD_NEW_FIELD }
  | { type: FormActionType.ADD_NEW_SETTING }
  | { type: FormActionType.DELETE_FIELD; payload: ControlModeTemplateField }
  | { type: FormActionType.DELETE_SETTING; payload: string }
  | {
      type: FormActionType.TOGGLE_QUICK_CONFIG;
      payload: ControlModeTemplateField;
    }
  | { type: FormActionType.RESET_FORM };

// Define the state structure
export interface FormState {
  // Template data
  name: string;
  formValues: Record<string, FormValue>;
  originalValues: Record<string, FormValue>;
  unsavedSettings: ControlModeTemplateSettings[] | null;
  groupedFields: {
    uiLabel: string;
    controllerField: string;
    fields: ControlModeTemplateField[];
  }[];

  // Field form state
  showAddField: boolean;
  isEditMode: boolean;
  editingField: ControlModeTemplateField | null;
  selectedSettingIndex: number;
  newFieldData: {
    uiLabel: string;
    controllerField: string;
    type: ControlModeTemplateFieldsType;
    defaultValue: string | number | boolean | null;
    quickConfig: boolean;
    range?: { min?: number; max?: number };
    listOptions?: string;
  };
  newFieldErrors: Record<string, string>;
  parsedOptions: string[];
  newOption: string;

  // Setting form state
  newSettingData: {
    uiLabel: string;
    controllerField: string;
  };
  newSettingErrors: Record<string, string>;
  showAddSetting: boolean;
  isSettingEditMode: boolean;
  editingSetting: ControlModeTemplateSettings | null;
  pendingSetting: ControlModeTemplateSettings | null;

  // UI state
  hasAttemptedSubmit: boolean;
  isSaving: boolean;
  fieldErrors: Record<string, string>;
  snackbars: Array<{
    id: string;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>;
  // Legacy snackbar properties (kept for backward compatibility)
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: 'success' | 'error' | 'info' | 'warning';
  deleteConfirmOpen: boolean;
  deleteItemType: 'field' | 'setting';
  itemToDelete: DeleteItem | null;
}
